import { DOCUMENT } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { CanvasInteractionEvent, Point } from "@pcp/core/interfaces";
import { animationFrameScheduler, BehaviorSubject, fromEvent, merge, Observable, ReplaySubject, Subscription } from "rxjs";
import { filter, map, observeOn, switchMap, take, tap } from "rxjs/operators";
import { PCP_LOGGER } from "@pcp/logging";
import { RubberElementInput } from "../interfaces";

/**
 * Width of the eraser sprite, in canvas units — NOT in screen pixels.
 *
 * It has to be fixed in canvas space so the teacher and the beamer put the eraser on the same spot
 * of the picture: the sprite is anchored at the erase point, so a size that is constant on screen
 * covers a different slice of the slide on each client (a small teacher preview scales 1 screen
 * pixel to ~2.5 canvas units, a full-size beamer to ~1) and the two drift visibly apart.
 *
 * Sized to sit around the erase brush, which is `brushRadius` 40, i.e. 80 units across.
 */
const CURSOR_SIZE_CANVAS_UNITS = 120;

/**
 * Where the erase point sits inside the sprite, as a fraction of its box.
 *
 * The artwork is an eraser tilted with its blue holder up-right and its orange working end
 * down-left, so the hole has to appear at that lower-left end — that is where a teacher expects the
 * rubber to bite. Centring the sprite instead leaves the icon sitting on top of what it just erased.
 */
const CURSOR_HOTSPOT_X = 0.1;
const CURSOR_HOTSPOT_Y = 0.78;


/**
 * Plain Rubber element implementation
 * to be included inside an iframe.
 */
@Component({
    selector: 'pcp-rubber-element-content',
    templateUrl: './rubber-element-content.component.html',
    styleUrls: ['./rubber-element-content.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RubberElementContentComponent implements AfterViewInit, OnDestroy {
    /** User interactions on the canvas. */
    #interactions = new BehaviorSubject<CanvasInteractionEvent>({
        drag: false,
        mousePosition: { x: 0, y: 0 },
        touch: false,
    });

    #rubberImage!: RubberElementInput;
    #interactionSubscription?: Subscription;
    #pointerExitSubscription?: Subscription;
    #cursorSprite?: HTMLImageElement;
    #lastCursorRect?: { x: number; y: number; width: number; height: number };
    readonly #log = inject(PCP_LOGGER).createScoped('slide-sync');

    // subscription to incoming (external) content updates
    #inputInteractionSubscription?: Subscription;

    /** User interactions on the canvas. */
    @Output() interactions: Observable<CanvasInteractionEvent> = this.#interactions.asObservable();

    @Input() set rubberImage(image: RubberElementInput) {
        this.#rubberImage = image;
        this.#inputInteractionSubscription?.unsubscribe();

        // Captured so the narrowing survives into the closure below.
        const updates = image.updates;
        if (updates) {
            this.#inputInteractionSubscription = this.afterViewInit$.pipe(
                take(1),
                switchMap(() => updates.pipe(
                    observeOn(animationFrameScheduler),
                    tap(update => {
                        if (update.drag) {
                            this.drawPoint(update.mousePosition);
                        }

                    }),
                    map(update => update.mousePosition),
                    tap(position => this.paintCursor(position)),
                )),
            ).subscribe();
        }
    }

    /** Cursor layer: the painted eraser, driven by local pointer moves and by remote updates. */
    @ViewChild('top', { static: true }) topCanvas!: ElementRef<HTMLCanvasElement>;

    @ViewChild('bottom', { static: true }) bottomCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('middle', { static: true }) middleCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('container', { static: true }) container!: ElementRef<HTMLDivElement>;

    // TODO: aendern bei window resize?
    private brushRadius!: number;
    private drag = false;
    private readonly document = inject(DOCUMENT);
    private afterViewInit$ = new ReplaySubject<void>();

    ngOnDestroy(): void {
        this.#interactionSubscription?.unsubscribe();
        this.#inputInteractionSubscription?.unsubscribe();
        this.#pointerExitSubscription?.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.createForegoundImageCanvas();
        this.createBackgroundImageCanvas();

        this.brushRadius = ((80 / 3840) * this.windowWidth);

        this.loadCursorSprite();
        this.listenOnMouseAndTouchInteractions();

        this.afterViewInit$.next();
    }

    private setDragEnabled = () => this.drag = true;
    private setDragDisabled = () => this.drag = false;

    private createForegoundImageCanvas(): void {
        const dirtyWindow = new Image();

        dirtyWindow.onload = () => {
            this.middleCtx.imageSmoothingEnabled = false;
            const canvasWidth = this.bottomCanvas.nativeElement.width;
            const canvasHeight = this.bottomCanvas.nativeElement.height;
            this.middleCtx.drawImage(dirtyWindow, 0, 0, canvasWidth, canvasHeight);
            this.middleCtx.globalCompositeOperation = "destination-out";
        }
        dirtyWindow.src = this.#rubberImage.foregroundImageUrl;
    }

    private createBackgroundImageCanvas(): void {
        const wally = new Image();

        wally.onload = () => {
            this.bottomCtx.imageSmoothingEnabled = false;
            const canvasWidth = this.bottomCanvas.nativeElement.width;
            const canvasHeight = this.bottomCanvas.nativeElement.height;
            this.bottomCtx.drawImage(wally, 0, 0, canvasWidth, canvasHeight);
        }
        wally.src = this.#rubberImage.backgroundImageUrl;
    }

    private drawPoint(position: Point): void {
        const gradient = this.middleCtx.createRadialGradient(position.x, position.y, 5, position.x, position.y, 60);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');

        this.middleCtx.beginPath();
        this.middleCtx.arc(position.x, position.y, this.brushRadius, 0, 2 * Math.PI);
        this.middleCtx.fillStyle = gradient;
        this.middleCtx.fill();
    }

    /**
     * Loads the eraser artwork and, once it is usable, hides the native cursor so the painted
     * sprite takes over. If the artwork never loads the CSS `cursor` from the stylesheet stays in
     * effect, so the user is never left without a cursor.
     */
    private loadCursorSprite(): void {
        const url = this.cursorSpriteUrl;
        if (!url) {
            this.#log.warn('Rubber cursor artwork URL missing; keeping the CSS cursor');
            return;
        }

        const sprite = new Image();
        sprite.src = url;
        sprite.decode()
            .then(() => {
                this.#cursorSprite = sprite;
                this.topCanvas.nativeElement.style.cursor = 'none';
            })
            .catch(() => this.#log.warn('Rubber cursor artwork failed to load; keeping the CSS cursor', { url }));
    }

    /**
     * The stylesheet hands us the bundler-resolved URL of the artwork through a custom property.
     * The asset lives in `libs/styles` and is emitted because the stylesheet references it, which
     * saves adding an asset glob to every one of the six apps that render this element.
     */
    private get cursorSpriteUrl(): string | null {
        const declared = this.canvasView
            ?.getComputedStyle(this.topCanvas.nativeElement)
            .getPropertyValue('--rubber-cursor-image') ?? '';

        return /url\(["']?([^"')]+)["']?\)/.exec(declared)?.[1] ?? null;
    }

    /**
     * The window the canvases actually live in. NOT `inject(DOCUMENT)`: this component is created
     * with `createComponent()` from the host's injector and only then moved into the iframe, so the
     * injected document is the host page's, not the one rendering these canvases.
     */
    private get canvasView(): (Window & typeof globalThis) | null {
        return this.topCanvas.nativeElement.ownerDocument.defaultView;
    }

    /**
     * Paints the eraser at `position`, or clears it when passed `null`.
     *
     * The eraser follows the pointer as a sprite on the top canvas rather than a CSS
     * `cursor: url(...)`, because Chrome suppresses custom cursors larger than 32x32 and silently
     * falls back to the default arrow over parts of the element while the canvas still receives
     * every event.
     *
     * `position` is in canvas units, which is also what is emitted to and received from other
     * clients, so the sprite lands on the same spot of the picture everywhere.
     *
     * The sprite is placed so its hotspot (see `CURSOR_HOTSPOT_X`/`Y`) lands on `position`, which is
     * where `drawPoint` erases. Drawing it from its top-left corner — the old CSS cursor's implicit
     * 0,0 hotspot — put the hole off the sprite's corner, well above-left of the eraser.
     *
     * Local pointer movement and remote updates share this one cursor layer; the last write wins,
     * which matches the previous behaviour.
     */
    private paintCursor(position: Point | null): void {
        if (this.#lastCursorRect) {
            const { x, y, width, height } = this.#lastCursorRect;
            // Clear a hair beyond the sprite so antialiased edges leave nothing behind.
            this.topCtx.clearRect(x - 1, y - 1, width + 2, height + 2);
            this.#lastCursorRect = undefined;
        }

        const sprite = this.#cursorSprite;
        if (!position || !sprite) {
            return;
        }

        const width = CURSOR_SIZE_CANVAS_UNITS;
        const height = width * (sprite.naturalHeight / sprite.naturalWidth);
        const x = position.x - width * CURSOR_HOTSPOT_X;
        const y = position.y - height * CURSOR_HOTSPOT_Y;

        this.topCtx.drawImage(sprite, x, y, width, height);
        this.#lastCursorRect = { x, y, width, height };
    }

    /**
     * Pointer position in canvas units. The iframe's viewport IS the 1920x1080 canvas and never
     * scrolls, so viewport coordinates are already canvas coordinates; `offsetLeft/Top` are the
     * container's own offset, which is 0 while the canvases are positioned at the origin.
     */
    private getPosition(event: MouseEvent): Point {
        return this.toCanvasPoint(event.clientX, event.clientY);
    }

    private getPositionTouch(event: TouchEvent): Point {
        return this.toCanvasPoint(event.touches[0].clientX, event.touches[0].clientY);
    }

    private toCanvasPoint(clientX: number, clientY: number): Point {
        return {
            x: clientX - this.container.nativeElement.offsetLeft,
            y: clientY - this.container.nativeElement.offsetTop,
        };
    }

    /**
     * Output movement.
     * property `drag` indicates if the mouse is pressed at the same time (user is erasing).
     *
     * This output can be used to transfer
     * - mouse cursor
     * - touch / click interactions (erasing image parts)
     *
     * to other clients.
     */
    private emitPosition = (position: Point) => this.#interactions.next({
        drag: this.drag,
        mousePosition: position,
        // TODO: remove touch, it has no additional value.
        touch: false,
    });

    private listenOnMouseAndTouchInteractions() {
        const el = this.container.nativeElement;
        const onMouseDown = fromEvent(el, 'mousedown').pipe(tap(this.setDragEnabled));
        const onMouseUp = fromEvent(el, 'mouseup').pipe(tap(this.setDragDisabled));
        const onMouseMove = fromEvent(el, 'mousemove');
        const onMouseLeave = fromEvent(this.topCanvas.nativeElement, 'mouseleave');

        const onTouchStart = fromEvent(el, 'touchstart').pipe(tap(this.setDragEnabled));
        const onTouchend = fromEvent(el, 'touchend').pipe(tap(this.setDragDisabled));
        const onTouchMove = fromEvent(el, 'touchmove');

        // NOTE: `onTouchend` is deliberately absent here - TouchEnd events carry no `clientX`/`clientY`.
        // It is subscribed separately below for its drag-flag side effect.
        const onTouchInteraction = merge(onTouchStart, onTouchMove).pipe(
            observeOn(animationFrameScheduler),
            map(event => this.getPositionTouch(event as TouchEvent))
        );

        const onMouseInteraction = merge(onMouseDown, onMouseUp, onMouseMove).pipe(
            observeOn(animationFrameScheduler),
            map(event => this.getPosition(event as MouseEvent)),
            tap(position => this.paintCursor(position))
        );

        // Two things that end an interaction but carry no position, so they are not part of the
        // stream above. `touchend` MUST still be subscribed: it is what clears the drag flag on lift,
        // and without it every later move keeps erasing.
        this.#pointerExitSubscription?.unsubscribe();
        this.#pointerExitSubscription = new Subscription();
        this.#pointerExitSubscription.add(
            onMouseLeave.pipe(observeOn(animationFrameScheduler)).subscribe(() => this.paintCursor(null))
        );
        this.#pointerExitSubscription.add(onTouchend.subscribe());

        this.#interactionSubscription?.unsubscribe();
        this.#interactionSubscription = merge(onMouseInteraction, onTouchInteraction).pipe(
            tap(this.emitPosition),
            filter(() => this.drag),
            tap(position => this.drawPoint(position))
        ).subscribe();
    }

    private get bottomCtx(): CanvasRenderingContext2D {
        return this.bottomCanvas.nativeElement.getContext('2d')!;
    }

    private get middleCtx(): CanvasRenderingContext2D {
        return this.middleCanvas.nativeElement.getContext('2d')!;
    }

    private get topCtx(): CanvasRenderingContext2D {
        return this.topCanvas.nativeElement.getContext('2d')!;
    }

    private get windowWidth() {
        return this.container.nativeElement?.clientWidth;
    }

}
